import React, { useEffect, useState } from 'react';
import { orderBy } from 'lodash/fp';
import { useQuery } from '@apollo/client';
import { Box } from '@material-ui/core';
import { useAuth } from '../../../../utils/hooks/auth';
import { Loader } from '../Loader';
import { ItemsListViewProps, QueryArgs, QueryComponentProps, QueryResult, isOrderQuery } from './types';
import { LIMIT } from './constants';
import ItemsListView from './ItemsListView';

const QueryComponent: React.FC<QueryComponentProps> = ({ queryArgs, query }) => {
    const [offset, setOffset] = useState(LIMIT);
    const [shouldLoad, setShouldLoad] = useState(true);

    useEffect(() => {
        setShouldLoad(true);
        setOffset(LIMIT);
    }, [queryArgs]);

    const {
        loading,
        error,
        data,
        fetchMore,
        called,
    } = useQuery<QueryResult, QueryArgs>(query, { variables: queryArgs });

    const { logout } = useAuth();
    if (!called && loading) {
        return (
            <Box display="flex" justifyContent='center' alignContent='center' flexGrow={1}>
                <Loader />
            </Box>
        );
    }

    if (error) {
        const authError = error.graphQLErrors[0]?.extensions?.exception?.status === 401;
        if(authError) {
            setTimeout(() => {
                logout();
            }, 500);
        }

        return (
            <div>
                {error.message}
            </div>
        );
    }

    if (data) {
        let items: ItemsListViewProps['items'] | undefined;
        let orderByString: string | undefined;

        if(isOrderQuery(data)){
            ({ orders: items } = data);
            orderByString = 'createdUtc';
        } else {
            ({ bills: items } = data);
            orderByString = 'issuedUtc';
        }

        const sortedItems = orderBy(orderByString, 'desc', items);
        const onLoadMore = async () => {
            if(shouldLoad) {
                const { data: fetchMoreResult } = await fetchMore({ variables: { offset } });
                let fetchMoreItems: ItemsListViewProps['items'] | undefined;

                if(isOrderQuery(fetchMoreResult)){
                    ({ orders: fetchMoreItems } = fetchMoreResult);
                } else {
                    ({ bills: fetchMoreItems } = fetchMoreResult);
                }

                const dataLen = fetchMoreItems.length;
                const newOffset = offset + dataLen;
                setOffset(newOffset);
                if(dataLen % LIMIT !== 0 || dataLen === 0){
                    setShouldLoad(false);
                }
            }
        };

        return (
            <ItemsListView
                hasMore={shouldLoad}
                loadMore={onLoadMore}
                items={sortedItems}
            />
        );
    }

    return null;
};

export default QueryComponent;
